import { invokeApi } from '@/lib/apiClient';

import { supabase } from '@/integrations/supabase/client';
import { UploadedFile } from './fileUploadService';

const LARGE_FILE_THRESHOLD = 100000; // 100K chars = likely large expediente
const MAX_TEXT_TO_SEND = 200000; // Updated limit to leverage VPS capacity (was 25k for edge functions)

export interface ExpedienteProcessResult {
  structuredText: string;
  documentsCount: number;
  documents: Array<{ title: string; type: string }>;
}

/**
 * Checks if a file should be processed as a large expediente
 */
export function isLargeExpediente(file: UploadedFile): boolean {
  return file.type === 'application/pdf' && file.extractedText.length > LARGE_FILE_THRESHOLD;
}

/**
 * Process large expediente files through the AI segmentation pipeline
 */
export async function processLargeExpediente(
  file: UploadedFile,
  onProgress?: (stage: string) => void
): Promise<UploadedFile> {
  if (!isLargeExpediente(file)) return file;

  onProgress?.('Analizando estructura del expediente...');

  try {
    // Truncate text to prevent memory limit exceeded in edge function
    const textToSend = file.extractedText.slice(0, MAX_TEXT_TO_SEND);
    console.log(`Sending ${textToSend.length} chars to process-expediente (original: ${file.extractedText.length})`);

    const { data, error: invokeError } = await invokeApi('process-expediente', {
      body: {
        text: textToSend,
        fileName: file.name,
      },
    });

    if (invokeError) {
      console.error('Expediente processing failed:', invokeError);
      return file;
    }

    const result = data as ExpedienteProcessResult;

    onProgress?.(`Expediente procesado: ${result.documentsCount} documentos identificados`);

    return {
      ...file,
      extractedText: `${result.structuredText}\n\n=== CONTENIDO ÍNTEGRO DEL EXPEDIENTE ===\n\n${file.extractedText}`,
    };
  } catch (error) {
    console.error('Error processing expediente:', error);
    // Fallback: return file with original text
    return file;
  }
}

/**
 * Process multiple files, detecting and processing large expedientes
 */
export async function processUploadedFiles(
  files: UploadedFile[],
  onProgress?: (stage: string) => void
): Promise<{ processedFiles: UploadedFile[]; expedientesProcessed: number }> {
  const processedFiles: UploadedFile[] = [];
  let expedientesProcessed = 0;

  for (const file of files) {
    if (isLargeExpediente(file)) {
      expedientesProcessed++;
      onProgress?.(`Procesando expediente ${expedientesProcessed}: ${file.name}...`);
      const processed = await processLargeExpediente(file, onProgress);
      processedFiles.push(processed);
    } else {
      processedFiles.push(file);
    }
  }

  return { processedFiles, expedientesProcessed };
}
